import React, { useCallback, useEffect, useState } from "react";
import { Alert, MenuItem, Snackbar, TextField, Box } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Panel, FormGrid, ListGrid, PrimaryButton, GhostButton } from "./ui";

// demo-ui.html deseni: üstte "Ekle / Güncelle" formu, altta kayıt listesi.
// Satıra tıklayınca kayıt forma yüklenir (id doluysa PUT, boşsa POST).
//
// config: { title, listTitle, sub, idKey, columns, fields, api:{list,create,update,remove} }
//   fields: [{ name, label, type:"text"|"number"|"kurus", options?, required?, parse?, format? }]
export default function FormListPanel({ config }) {
  const { idKey, columns, fields, api, title, listTitle, sub } = config;

  const emptyForm = useCallback(
    () => fields.reduce((a, f) => ({ ...a, [f.name]: f.default ?? "" }), { [idKey]: "" }),
    [fields, idKey]
  );

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list());
    } catch (e) {
      showSnackbar(e.message || "Liste alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [api, showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setForm(emptyForm());

  const handleRowClick = ({ row }) => {
    setForm(
      fields.reduce(
        (a, f) => {
          const raw = row[f.name];
          a[f.name] = f.format && raw != null ? f.format(raw) : raw ?? "";
          return a;
        },
        { [idKey]: row[idKey] }
      )
    );
  };

  const handleSave = async () => {
    const missing = fields.find((f) => f.required && form[f.name] === "");
    if (missing) {
      showSnackbar(`${missing.label} zorunlu`, "error");
      return;
    }

    // Form değerleri string tutulur; API'ye tipiyle gider.
    const dto = fields.reduce((a, f) => {
      const raw = form[f.name];
      if (f.type === "number" || f.type === "select") a[f.name] = raw === "" ? null : Number(raw);
      else if (f.parse) a[f.name] = f.parse(raw);
      else a[f.name] = raw;
      return a;
    }, {});

    const isUpdate = form[idKey] !== "" && form[idKey] != null;
    setSaving(true);
    try {
      if (isUpdate) {
        await api.update({ ...dto, [idKey]: Number(form[idKey]) });
        showSnackbar("Kayıt güncellendi", "success");
      } else {
        await api.create(dto);
        showSnackbar("Kayıt eklendi", "success");
      }
      reset();
      load();
    } catch (e) {
      showSnackbar(e.message || "Kaydedilemedi", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.remove(id);
      showSnackbar("Kayıt silindi", "success");
      if (Number(form[idKey]) === Number(id)) reset();
      load();
    } catch (e) {
      showSnackbar(e.message || "Silinemedi", "error");
    }
  };

  const gridColumns = [
    ...columns,
    {
      field: "__actions",
      type: "actions",
      headerName: "",
      width: 60,
      getActions: (params) => [
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon fontSize="small" />}
          label="Sil"
          onClick={(e) => {
            e.stopPropagation(); // satır tıklaması forma yüklemesin
            handleDelete(params.row[idKey]);
          }}
        />,
      ],
    },
  ];

  const isUpdate = form[idKey] !== "" && form[idKey] != null;

  return (
    <>
      <Panel
        title={`${title} Ekle / Güncelle`}
        sub={sub || "Boş kayıt eklenir, listeden seçilen kayıt güncellenir."}
      >
        <FormGrid>
          {fields.filter((f) => !f.hidden).map((f) => (
            <TextField
              key={f.name}
              select={f.type === "select"}
              type={f.type === "number" ? "number" : "text"}
              label={f.label}
              value={form[f.name] ?? ""}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              required={f.required}
              disabled={f.disabled}
              helperText={f.helperText}
              size="small"
              fullWidth
            >
              {f.type === "select" &&
                (f.options || []).map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
            </TextField>
          ))}
        </FormGrid>

        <Box sx={{ display: "flex", gap: 1.5, mt: 2.5 }}>
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {isUpdate ? "Güncelle" : "Kaydet"}
          </PrimaryButton>
          <GhostButton onClick={reset}>Temizle</GhostButton>
        </Box>
      </Panel>

      <Panel
        title={listTitle || `Kayıtlı ${title}`}
        sub="Düzenlemek için satıra tıklayın."
        action={
          <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Yenile
          </GhostButton>
        }
      >
        <ListGrid
          rows={rows}
          columns={gridColumns}
          getRowId={(r) => r[idKey]}
          loading={loading}
          onRowClick={handleRowClick}
        />
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
